import React, { useEffect, useMemo, useState } from "react";
import { FaTimes, FaPlus, FaMinus, FaMicrochip } from "react-icons/fa";
import { CustomDropdown, NumberInput, Textarea } from "../../atoms";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";

/**
 * Cihaz bazında kota transferi — bayi paneli ve merkezin bayi cihazı (#335,
 * Device/storageRoles/CenterDealerQuotaDrawer) kullanır.
 *
 * Firma bakiyesi (dağıtılmayan kota) ile TEK cihaz arasında, miktarı kullanıcı
 * belirleyerek kota taşır. Otomatik dağıtımdan farkı budur: hangi cihaza ne
 * kadar gideceğine bayi karar verir.
 *
 * Toplam kota değişmez — bu yüzden fatura üretmez, yalnızca yer değiştirir
 * (sunucu: dealer-roles/device-quota-dealer.controller).
 *
 * @param direction "give" (bakiye → cihaz) | "take" (cihaz → bakiye)
 * @param device    hedef cihaz (productInfo populate edilmiş liste objesi)
 * @param firmProducts ProductFirm.productInfo — firmanın ürün bakiyeleri
 */
const DeviceQuotaDrawer = ({
  open,
  onClose,
  onSubmit,
  direction: directionProp = "give",
  device: deviceProp = null,
  firmProducts = [],
  isLoading = false,
  // Merkezde bayi cihazı için "Bayi bakiyesi" (#335)
  balanceLabel = "Firma bakiyesi",
}) => {
  const frozenInput = useMemo(
    () => ({ direction: directionProp, device: deviceProp }),
    [directionProp, deviceProp]
  );
  const { mounted, entered, data: frozen } = useDrawerTransition(open, frozenInput);
  const direction = frozen?.direction ?? directionProp;
  const device = frozen?.device ?? deviceProp;

  const [productName, setProductName] = useState("");
  const [quota, setQuota] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});

  const isGive = direction === "give";

  // Ürün listesi cihazdan gelir — cihazda olmayan ürüne kota yüklenemez.
  const deviceProducts = useMemo(
    () => device?.productInfo?.productInfo || [],
    [device]
  );
  const productOptions = deviceProducts.map((p) => ({
    id: p.productName,
    label: p.productName,
    value: p.productName,
  }));

  const deviceProduct = deviceProducts.find((p) => p.productName === productName);
  const firmProduct = (firmProducts || []).find((p) => p.productName === productName);

  const deviceQuota = Number(deviceProduct?.quota) || 0;
  const firmQuota = Number(firmProduct?.quota) || 0;
  const quotaMax = Number(deviceProduct?.quotaMax) || 0;

  // Yönüne göre üst sınır: verirken bakiye (ve varsa cihazın maksimum kota
  // boşluğu), alırken cihazdaki kota.
  const maxTransferable = isGive
    ? quotaMax > 0
      ? Math.max(0, Math.min(firmQuota, quotaMax - deviceQuota))
      : firmQuota
    : deviceQuota;

  const amount = Number(quota) || 0;
  const nextDeviceQuota = isGive ? deviceQuota + amount : deviceQuota - amount;
  const nextFirmQuota = isGive ? firmQuota - amount : firmQuota + amount;

  // Sıfırlama kapanış kayması bittikten sonra — aksi halde drawer ekrandayken
  // form boşalıyor (bkz. hooks/useDrawerTransition).
  useEffect(() => {
    if (!mounted) {
      setProductName("");
      setQuota("");
      setNote("");
      setErrors({});
    }
  }, [mounted]);

  // Tek ürünlü cihazda seçim adımını atla.
  useEffect(() => {
    if (open && !productName && deviceProducts.length === 1) {
      setProductName(deviceProducts[0].productName);
    }
  }, [open, productName, deviceProducts]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  if (!mounted || !device) return null;

  const validate = () => {
    const next = {};
    if (!productName) next.productName = "Lütfen bir ürün seçin.";
    if (!amount || amount <= 0) next.quota = "Kota 0'dan büyük olmalı.";
    else if (isGive && amount > firmQuota)
      next.quota = `${balanceLabel} yetersiz (bakiye: ${firmQuota.toLocaleString("tr-TR")}).`;
    else if (isGive && quotaMax > 0 && deviceQuota + amount > quotaMax)
      next.quota = `Cihazın maksimum kotası aşılıyor (maksimum: ${quotaMax.toLocaleString("tr-TR")}).`;
    else if (!isGive && amount > deviceQuota)
      next.quota = `Cihazda bu kadar kota yok (cihaz kotası: ${deviceQuota.toLocaleString("tr-TR")}).`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onSubmit({ productName, quota: amount, note });
  };

  const Icon = isGive ? FaPlus : FaMinus;
  const headerGradient = isGive
    ? "from-emerald-600 to-green-700"
    : "from-amber-500 to-orange-600";
  const submitClass = isGive
    ? "bg-emerald-500 hover:bg-emerald-600"
    : "bg-amber-500 hover:bg-amber-600";

  return (
    <>
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[440px] transform ${
          entered ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out shadow-2xl z-[9999] flex flex-col bg-white`}
      >
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-emerald-500 rounded-full animate-spin" />
            <p className="mt-4 text-sm font-Medium text-gray-700">İşleniyor...</p>
          </div>
        )}

        {/* Header */}
        <div className={`bg-gradient-to-r ${headerGradient} px-5 py-4 text-white shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm shrink-0">
                <Icon className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-Bold tracking-wide">
                  {isGive ? "Cihaza Kota Ver" : "Cihazdan Kota Al"}
                </h3>
                <p className="text-xs text-white/80 truncate">
                  {device.customName || device.deviceName}
                  <span className="ml-1.5 font-mono text-white/60">{device.serialNo}</span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors shrink-0"
              aria-label="Kapat"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
            <div>
              <CustomDropdown
                label="Ürün"
                required
                value={productName}
                options={productOptions}
                onChange={(opt) => {
                  setProductName(opt?.value || "");
                  setQuota("");
                  setErrors((p) => ({ ...p, productName: undefined, quota: undefined }));
                }}
                placeholder="Ürün seçin"
                error={!!errors.productName}
                errorMessage={errors.productName}
                emptyMessage="Cihazda ürün tanımlı değil"
              />
            </div>

            {productName && (
              <div className="rounded-xl border border-gray-200 bg-gray-50/70 divide-y divide-gray-200">
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs font-Medium text-gray-500 tracking-wide">
                    FİRMA BAKİYESİ
                  </span>
                  <span className="text-sm font-SemiBold text-gray-800 tabular-nums">
                    {firmQuota.toLocaleString("tr-TR")}
                  </span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs font-Medium text-gray-500 tracking-wide">
                    CİHAZ KOTASI
                  </span>
                  <span className="text-sm font-SemiBold text-gray-800 tabular-nums">
                    {deviceQuota.toLocaleString("tr-TR")}
                  </span>
                </div>
                {quotaMax > 0 && (
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-xs font-Medium text-gray-500 tracking-wide">
                      CİHAZ MAKSİMUM KOTASI
                    </span>
                    <span className="text-sm font-SemiBold text-gray-800 tabular-nums">
                      {quotaMax.toLocaleString("tr-TR")}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs font-Medium text-gray-500 tracking-wide">
                    {isGive ? "VERİLEBİLECEK EN FAZLA" : "ALINABİLECEK EN FAZLA"}
                  </span>
                  <span
                    className={`text-sm font-Bold tabular-nums ${
                      maxTransferable > 0 ? "text-indigo-600" : "text-red-500"
                    }`}
                  >
                    {maxTransferable.toLocaleString("tr-TR")}
                  </span>
                </div>
              </div>
            )}

            <div>
              <NumberInput
                label={isGive ? "Verilecek Kota" : "Alınacak Kota"}
                required
                value={quota}
                onChange={(v) => {
                  setQuota(v);
                  if (errors.quota) setErrors((p) => ({ ...p, quota: undefined }));
                }}
                min={0}
                max={maxTransferable || undefined}
                placeholder="0"
                error={!!errors.quota}
                errorMessage={errors.quota}
              />
              {maxTransferable > 0 && (
                <button
                  type="button"
                  onClick={() => setQuota(maxTransferable)}
                  className="mt-1.5 text-xs font-SemiBold text-indigo-600 hover:text-indigo-800"
                >
                  Tümünü kullan ({maxTransferable.toLocaleString("tr-TR")})
                </button>
              )}
            </div>

            {amount > 0 && !errors.quota && (
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <FaMicrochip className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                  <span className="text-xs font-Bold text-indigo-700 tracking-wide">
                    İŞLEM SONRASI
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{balanceLabel}</span>
                  <span className="font-SemiBold text-gray-900 tabular-nums">
                    {firmQuota.toLocaleString("tr-TR")} → {nextFirmQuota.toLocaleString("tr-TR")}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-gray-600">Cihaz kotası</span>
                  <span className="font-SemiBold text-gray-900 tabular-nums">
                    {deviceQuota.toLocaleString("tr-TR")} → {nextDeviceQuota.toLocaleString("tr-TR")}
                  </span>
                </div>
              </div>
            )}

            <Textarea
              label="Açıklama"
              value={note}
              onChange={(e) => setNote(e.target.value ?? "")}
              placeholder="İsteğe bağlı"
              rows={3}
            />

            <p className="text-xs text-gray-500 leading-relaxed">
              Bu işlem firmanın toplam kotasını değiştirmez; kota yalnızca bakiye
              ile cihaz arasında yer değiştirir. İşlem canlıdır: cihaz
              çevrimdışıysa yapılmaz.
            </p>
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-gray-200 px-5 py-4 flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-SemiBold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`flex-1 py-2.5 rounded-xl text-sm font-SemiBold text-white transition-colors disabled:opacity-50 ${submitClass}`}
            >
              {isGive ? "Kotayı Ver" : "Kotayı Al"}
            </button>
          </div>
        </form>
      </div>

      {mounted && (
        <div
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] transition-opacity duration-300 ease-in-out ${
            entered ? "opacity-100" : "opacity-0"
          }`}
          onClick={isLoading ? undefined : onClose}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default DeviceQuotaDrawer;
